import { useTranslation } from '../../../shared/i18n';
import React, { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button, Card, Col, Grid, Input, message, Row, Space, Typography, theme } from 'antd';
import { ArrowLeftOutlined, CheckCircleFilled, RocketOutlined } from '@ant-design/icons';
import Editor from '@monaco-editor/react';
import cdnService from '../../services/cdnService';
import { userPath } from '../../../shared/config/paths';
import { useTheme } from '../../../theme/ThemeContext';
import { WORKER_TEMPLATES } from '../../../shared/constants/workerTemplates';

const { Title, Text, Paragraph } = Typography;
const { useBreakpoint } = Grid;

const NAME_RE = /^[a-z0-9][a-z0-9-]{0,62}$/;

function randomName() {
  const words = ['swift', 'calm', 'bright', 'quiet', 'bold', 'lucky', 'rapid', 'silent'];
  const nouns = ['edge', 'river', 'cloud', 'spark', 'wave', 'peak', 'field', 'grove'];
  const pick = (a) => a[Math.floor(Math.random() * a.length)];
  return `${pick(words)}-${pick(nouns)}-${Math.floor(Math.random() * 900 + 100)}`;
}

/**
 * Cloudflare-style "Create Worker": pick a starter, name it, deploy.
 * Routes, variables and code editing happen afterwards on the worker page.
 */
export default function WorkerCreate() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const screens = useBreakpoint();
  const isMobile = !screens.md;
  const { token } = theme.useToken();
  const { themeMode } = useTheme();

  const [templateKey, setTemplateKey] = useState('custom-response');
  const [name, setName] = useState(randomName);
  const [deploying, setDeploying] = useState(false);

  const template = useMemo(
    () => WORKER_TEMPLATES.find((t) => t.key === templateKey) || WORKER_TEMPLATES[0],
    [templateKey],
  );
  const nameValid = NAME_RE.test(name);

  const deploy = async () => {
    if (!nameValid) {
      message.error(t('Names can only contain lowercase letters, numbers, and -, and must start with a letter or number'));
      return;
    }
    setDeploying(true);
    const res = await cdnService.saveWorker({
      name,
      description: t(template.description),
      code: template.code,
      enabled: true,
      message: t('Created from template "{{label}}"', { label: t(template.label) }),
    });
    setDeploying(false);
    if (res.success && res.data?.id) {
      message.success(t('Worker deployed'));
      navigate(userPath(`/cdn/workers/${res.data.id}`), { replace: true });
    } else {
      message.error(res.error || t('Deployment failed'));
    }
  };

  return (
    <div>
      <Space style={{ marginBottom: 16 }}>
        <Button type="text" icon={<ArrowLeftOutlined />} onClick={() => navigate(userPath('/cdn/workers'))}>{t('Edge functions')}</Button>
      </Space>
      <Title level={isMobile ? 4 : 3} style={{ marginTop: 0 }}>{t('Create Worker')}</Title>
      <Paragraph type="secondary" style={{ marginTop: -4 }}>{t('Choose a starting point and name it to deploy. After deployment, you can edit code, run online tests, bind routes, and configure variables on the Worker page.')}</Paragraph>

      <Row gutter={[24, 24]}>
        <Col xs={24} lg={10}>
          <Text strong>{t('1. Select a template')}</Text>
          <Row gutter={[12, 12]} style={{ marginTop: 12 }}>
            {WORKER_TEMPLATES.map((templateItem) => {
              const active = templateItem.key === templateKey;
              return (
                <Col xs={24} sm={12} key={templateItem.key}>
                  <Card
                    hoverable
                    size="small"
                    onClick={() => setTemplateKey(templateItem.key)}
                    style={{
                      height: '100%',
                      borderColor: active ? token.colorPrimary : undefined,
                      boxShadow: active ? `0 0 0 1px ${token.colorPrimary}` : undefined,
                    }}
                  >
                    <Space align="start" style={{ width: '100%', justifyContent: 'space-between' }}>
                      <div>
                        <Text strong>{t(templateItem.label)}</Text>
                        <div><Text type="secondary" style={{ fontSize: 12 }}>{t(templateItem.description)}</Text></div>
                      </div>
                      {active && <CheckCircleFilled style={{ color: token.colorPrimary }} />}
                    </Space>
                  </Card>
                </Col>
              );
            })}
          </Row>
        </Col>

        <Col xs={24} lg={14}>
          <Text strong>{t('2. Name and deploy')}</Text>
          <Card size="small" style={{ marginTop: 12 }}>
            <Text type="secondary" style={{ fontSize: 12 }}>{t('Worker name')}</Text>
            <Input
              value={name}
              onChange={(e) => setName(e.target.value.trim().toLowerCase())}
              status={nameValid ? undefined : 'error'}
              style={{ marginTop: 4, marginBottom: 4 }}
              maxLength={63}
            />
            <Text type={nameValid ? 'secondary' : 'danger'} style={{ fontSize: 12 }}>{t('Lowercase letters, numbers, and -, up to 63 characters')}</Text>

            <div style={{ marginTop: 16, marginBottom: 8 }}>
              <Text type="secondary" style={{ fontSize: 12 }}>{t('Code preview (editable after deployment)')}</Text>
            </div>
            <div style={{ border: `1px solid ${token.colorBorderSecondary}`, borderRadius: token.borderRadius, overflow: 'hidden' }}>
              <Editor
                height={isMobile ? '260px' : '360px'}
                language="javascript"
                theme={themeMode === 'dark' ? 'vs-dark' : 'light'}
                value={template.code}
                options={{ readOnly: true, minimap: { enabled: false }, fontSize: 13, scrollBeyondLastLine: false, automaticLayout: true }}
              />
            </div>

            <Button
              type="primary"
              icon={<RocketOutlined />}
              size="large"
              block
              loading={deploying}
              disabled={!nameValid}
              onClick={deploy}
              style={{ marginTop: 16 }}
            >{t('Deploy')}</Button>
          </Card>
        </Col>
      </Row>
    </div>
  );
}
